import React, { useState, useEffect } from "react";
import axios from "axios";
import { Table } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import HeaderMain from "../components/header/HeaderMain";
import NavMain from "../components/nav/NavMain";
import asset from "../asset.json";
import GLOBALVAR from "../constants"; // Import GLOBALVAR constant
import ToPeso from "../components/misc/ToPeso";

const jsonData = asset;


function AssetDropdown() {
    // Multi-level dropdown states
    const [category, setCategory] = useState("");
    const [subcategory, setSubcategory] = useState("");
    const [item, setItem] = useState(null);
    const [amount, setAmount] = useState("");
    const [dateAdded, setDateAdded] = useState("");
    const [credit, setCredit] = useState(true); // Default to credit
    const [remarks, setRemarks] = useState("");
    const [responseMessage, setResponseMessage] = useState(null);
    const [transactionLogs, setTransactionLogs] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const logsPerPage = 10;

    // Get top-level categories
    const categories = Object.keys(jsonData.Asset);

    // Get subcategories or items for selected category
    let subcategories = [];
    let items = [];
    if (category) {
        const catData = jsonData.Asset[category];
        if (Array.isArray(catData)) {
            // No subcategory, direct items
            items = catData;
        } else if (typeof catData === "object") {
            subcategories = Object.keys(catData);
            if (subcategory && catData[subcategory]) {
                if (Array.isArray(catData[subcategory])) {
                    items = catData[subcategory];
                } else if (typeof catData[subcategory] === "object") {
                    // For deeper nesting (not present in asset.json, but future-proof)
                    const subcatData = catData[subcategory];
                    const subsubcategories = Object.keys(subcatData);
                    // Only support one more level for now
                    if (subsubcategories.length === 1 && Array.isArray(subcatData[subsubcategories[0]])) {
                        items = subcatData[subsubcategories[0]];
                    }
                }
            }
        }
    }

    // Helper to get full label path for the selected item
    const getFullLabel = () => {
        if (!item) return "";
        let label = "";
        if (category) label += category;
        if (subcategory) label += ` > ${subcategory}`;
        label += ` > ${item.label}`;
        return label;
    };


    const fetchTransactionLogs = async (page = 1) => {
        try {
            const response = await axios.get(
                `${GLOBALVAR["SERVER_ROOT"]}/api/asset-transactions/fetch?page=${page}&limit=${logsPerPage}`
            );
            setTransactionLogs(response.data.logs);
            setCurrentPage(response.data.currentPage);
            setTotalPages(response.data.totalPages);
        } catch (error) {
            console.error("Error fetching transaction logs:", error);
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!item) {
            setResponseMessage({ type: "error", text: "Please select an asset item." });
            return;
        }
        try {
            const payload = {
                item_id: item.id,
                label: getFullLabel(),
                amount,
                date_added: dateAdded,
                credit,
                remarks,
            };
            await axios.post(`${GLOBALVAR["SERVER_ROOT"]}/api/asset-transactions`, payload);
            setResponseMessage({ type: "success", text: "Transaction successfully added!" });
            // Reset form fields
            setCategory("");
            setSubcategory("");
            setItem(null);
            setAmount("");
            setDateAdded("");
            setCredit(true);
            setRemarks("");
            fetchTransactionLogs();
        } catch (error) {
            console.error("Error adding transaction:", error);
            setResponseMessage({ type: "error", text: "Failed to add transaction. Please try again." });
        }
    };


    useEffect(() => {
        fetchTransactionLogs();
    }, []);


    const handlePageChange = (pageNumber) => {
        fetchTransactionLogs(pageNumber);
    };

    return (
        <div className="container mt-4 mb-5">
            <h4 className="mb-4">Add an Asset Transaction</h4>
            <form onSubmit={handleSubmit} className="p-3 rounded shadow-sm bg-light">
                <div className="row g-3 align-items-end">
                    <div className="col-md-4">
                        <label className="form-label">Category</label>
                        <select
                            className="form-select"
                            value={category}
                            onChange={e => {
                                setCategory(e.target.value);
                                setSubcategory("");
                                setItem(null);
                            }}
                            required
                        >
                            <option value="">Select category...</option>
                            {categories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                    </div>
                    {subcategories.length > 0 && (
                        <div className="col-md-4">
                            <label className="form-label">Subcategory</label>
                            <select
                                className="form-select"
                                value={subcategory}
                                onChange={e => {
                                    setSubcategory(e.target.value);
                                    setItem(null);
                                }}
                                required
                            >
                                <option value="">Select subcategory...</option>
                                {subcategories.map(subcat => (
                                    <option key={subcat} value={subcat}>{subcat}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div className="col-md-4">
                        <label className="form-label">Item</label>
                        <select
                            className="form-select"
                            value={item ? item.id : ""}
                            onChange={e => {
                                const selectedId = parseInt(e.target.value, 10);
                                const found = items.find(i => i.id === selectedId);
                                setItem(found || null);
                            }}
                            required
                            disabled={items.length === 0}
                        >
                            <option value="">Select item...</option>
                            {items.map(i => (
                                <option key={i.id} value={i.id}>{i.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="row g-3 mt-2">
                    <div className="col-md-4">
                        <label className="form-label">Amount</label>
                        <input
                            type="number"
                            className="form-control"
                            value={amount}
                            onChange={e => setAmount(e.target.value)}
                            placeholder="Enter the amount"
                            required
                            min="0"
                        />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">Date Added</label>
                        <input
                            type="date"
                            className="form-control"
                            value={dateAdded}
                            onChange={e => setDateAdded(e.target.value)}
                            required
                        />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">Credit/Debit</label>
                        <select
                            className="form-select"
                            value={credit ? "true" : "false"}
                            onChange={e => setCredit(e.target.value === "true")}
                            required
                        >
                            <option value="true">Credit</option>
                            <option value="false">Debit</option>
                        </select>
                    </div>
                </div>
                <div className="row g-3 mt-2">
                    <div className="col-12">
                        <label className="form-label">Remarks</label>
                        <textarea
                            className="form-control"
                            value={remarks}
                            onChange={e => setRemarks(e.target.value)}
                            placeholder="Add remarks (optional)"
                            rows={2}
                        />
                    </div>
                </div>
                <div className="row mt-4">
                    <div className="col-12 d-flex justify-content-end">
                        <button type="submit" className="btn btn-primary px-4">
                            Submit
                        </button>
                    </div>
                </div>
            </form>
            {responseMessage && (
                <div className={`alert mt-3 ${responseMessage.type === "success" ? "alert-success" : "alert-danger"}`}>
                    {responseMessage.text}
                </div>
            )}
            <h5 className="mt-5 mb-3">Transaction Logs</h5>
            {transactionLogs.length > 0 ? (
                <div className="card shadow-sm border-0 mb-4">
                    <div className="table-responsive">
                        <Table className="list-container table-striped table-hover align-middle mb-0" responsive>
                            <thead className="table-light">
                                <tr>
                                    <th className="text-center">#</th>
                                    <th>Label</th>
                                    <th>Date Added</th>
                                    <th className="text-center">Type</th>
                                    <th className="text-end">Amount</th>
                                    <th>Remarks</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactionLogs.map((log, index) => {
                                    // Split label for better readability
                                    let labelLines = log.label.split(' > ');
                                    return (
                                        <tr key={log.id} style={{height: '72px'}}>
                                            <td className="text-center fw-bold" style={{verticalAlign: 'middle', paddingTop: '22px', paddingBottom: '22px'}}>{log.id}</td>
                                            <td style={{minWidth: '240px', paddingTop: '18px', paddingBottom: '18px'}}>
                                                <span className="fw-semibold" style={{fontSize: '1.12em', lineHeight: 1.3}}>
                                                    {labelLines.length > 1 ? (
                                                        <>
                                                            <span className="text-secondary" style={{fontSize: '0.99em'}}>
                                                                {labelLines.slice(0, -1).join(' > ')}
                                                            </span>
                                                            <br />
                                                            <span className="fw-bold text-dark" style={{fontSize: '1.18em'}}>
                                                                {labelLines[labelLines.length - 1]}
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <span className="fw-bold text-dark">{log.label}</span>
                                                    )}
                                                </span>
                                            </td>
                                            <td style={{verticalAlign: 'middle', paddingTop: '22px', paddingBottom: '22px'}}>{log.date_added}</td>
                                            <td className="text-center" style={{verticalAlign: 'middle', paddingTop: '22px', paddingBottom: '22px'}}>
                                                <span className={`badge rounded-pill px-3 py-2 ${log.credit ? 'bg-success' : 'bg-danger'}`}
                                                    style={{fontSize: '1.08em', letterSpacing: '0.03em'}}>
                                                    {log.credit ? 'Credit' : 'Debit'}
                                                </span>
                                            </td>
                                            <td className="text-end" style={{verticalAlign: 'middle', paddingTop: '22px', paddingBottom: '22px', fontWeight: 600, fontSize: '1.13em'}}>
                                                <ToPeso amount={log.amount} />
                                            </td>
                                            <td style={{verticalAlign: 'middle', paddingTop: '22px', paddingBottom: '22px'}}>
                                                {log.remarks ? <span className="text-muted">{log.remarks}</span> : <span className="text-secondary">N/A</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                    <nav className="mt-4 mb-2">
                        <ul className="pagination justify-content-end gap-2 mb-0" style={{fontSize: '1.08em'}}>
                            {Array.from({ length: totalPages }, (_, i) => (
                                <li
                                    key={i + 1}
                                    className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                                    style={{marginLeft: 4, marginRight: 4}}
                                    onClick={() => handlePageChange(i + 1)}
                                >
                                    <button className="page-link px-3 py-2" style={{borderRadius: 8, minWidth: 38}}>{i + 1}</button>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </div>
            ) : (
                <div className="alert alert-info">No transaction logs available.</div>
            )}
        </div>
    );
}

function AssetManagement() {
    return (
        <>
            <NavMain slug="asset-management" parent="7" />
            <HeaderMain slug="asset-management" />
            <div className="main-content financials">
                <AssetDropdown />
            </div>
        </>
    );
}

export default AssetManagement;